<script setup lang="ts">
import CalendarDayNumber from 'frontcore/presentation/calendar/views/CalendarDayNumber.vue'
import CalendarHeaderDaySelector from 'frontcore/presentation/calendar/views/CalendarHeaderDaySelector.vue'
import CalendarHeaderMonthSelector from 'frontcore/presentation/calendar/views/CalendarHeaderMonthSelector.vue'
import CalendarHeaderYearSelector from 'frontcore/presentation/calendar/views/CalendarHeaderYearSelector.vue'
import i18n from 'frontcore/i18n'
import RangeSelectorSelectedDate from 'frontcore/presentation/calendar/views/RangeSelectorSelectedDate.vue'
import { CalendarModeOptions, CoreCalendarViewModel, DateRange, DayObject } from 'frontcore/presentation/calendar/domain/types'
import { getEndOfMonth, getEndOfWeek, getEndOfYear, getStartOfWeek, getStartOfYear } from 'frontcore/domain/dates/date-utils'
import { ref }  from 'vue'

interface Props {
  calendarModel: CoreCalendarViewModel
  range?: boolean
  selectionMode?: CalendarModeOptions
  disablePastDays?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  range: false,
  selectionMode: CalendarModeOptions.SELECT_DAY,
  disablePastDays: false,
})

const hoveredWeekNumber = ref(0)

const emit = defineEmits<{
  (e: 'input:value', date: Date): void,
  (e: 'selectStartRange', date: DateRange): void,
  (e: 'selectEndRange', date: DateRange): void,
  (e: 'updateMonth', month: number): void,
  (e: 'updateYear', year: number): void,
  (e: 'updateMode', mode: CalendarModeOptions): void,
}>()

const sortDates = (currentDates: DateRange): DateRange => {
  if (Array.isArray(currentDates) && currentDates.length == 2 && currentDates[0] && currentDates[1]) {
    if (currentDates[1] < currentDates[0]) {
      currentDates[0].setHours(23,59,59,999)
      return [currentDates[1], currentDates[0]]
    }
    currentDates[1].setHours(23,59,59,999)
    return [currentDates[0], currentDates[1]]
  }
  return currentDates
}

const selectWeek = (startDate: Date|null, disabled: boolean) => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_WEEK || startDate === null || disabled) {
    return
  }
  startDate = getStartOfWeek(startDate)
  const endDate = getEndOfWeek(startDate)
  emit('selectEndRange', [startDate, endDate])
}

const selectDate = (date: Date|null, disabled: boolean) => {
  if (date === null || disabled) return
  if (props.selectionMode === CalendarModeOptions.SELECT_WEEK) {
    return selectWeek(date, false)
  }
  if (!props.range) {
    emit('input:value', date)
  }
  else if (!props.calendarModel.startDate
    ||(props.calendarModel.endDate && !isUpdatingEndDate.value)
    || isUpdatingStartDate.value) {
    let selectedRange: DateRange = [date]
    if (props.calendarModel.endDate && isUpdatingStartDate.value) {
      selectedRange = sortDates([date, props.calendarModel.endDate])
    }
    emit('selectStartRange', selectedRange)
    isUpdatingStartDate.value = false
    isUpdatingEndDate.value = true
  } else {
    emit('selectEndRange', sortDates([props.calendarModel.startDate, date]))
    isUpdatingEndDate.value = false
    isUpdatingStartDate.value = true
  }
}

const selectToday = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  selectDate(today, props.calendarModel.isTodayDisabled)
}

const isDateDisabled = (calendarDate: DayObject): boolean => {
  if (props.selectionMode === CalendarModeOptions.SELECT_WEEK) {
    return calendarDate.isWeekDisabled
  }
  return calendarDate.isDisabled
}

const selectMonth = () => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_MONTH) {
    return
  }
  const startDate = new Date()
  startDate.setFullYear(props.calendarModel.currentPickerYear, props.calendarModel.currentPickerMonth - 1, 1)
  startDate.setHours(0, 0, 0, 0)
  const endDate = getEndOfMonth(startDate)
  emit('selectEndRange', [startDate, endDate])
}

const { t } = i18n.global

const isUpdatingStartDate = ref(false)
const isUpdatingEndDate = ref(false)

const updateStartDate = () => {
  isUpdatingStartDate.value = true
  isUpdatingEndDate.value = false
}

const updateEndDate = () => {
  if (!props.calendarModel.startDate) return
  isUpdatingEndDate.value = true
  isUpdatingStartDate.value = false
}

const updateMonth = (month: number) => {
  emit('updateMonth', month)
}
const updateMode = (mode: CalendarModeOptions) => {
  emit('updateMode', mode)
}
const updateYear = (year: number) => {
  emit('updateYear', year)
}
const selectYear= (year: number) => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_YEAR) {
    return
  }
  const date = new Date(year, 1, 1)
  const startDate = getStartOfYear(date)
  const endDate = getEndOfYear(date)
  emit('selectEndRange', [startDate, endDate])
}

const handleHover = (weekNumber: number) => {
  if (props.selectionMode !== CalendarModeOptions.SELECT_WEEK) {
    return 0
  }
  hoveredWeekNumber.value = weekNumber
}

</script>

<template>
  <div
    data-test="calendar-menu"
    class="calendar__menu s-p-4 bg-core-neutral-cold-100">
    <div
      v-show="calendarModel.mode === CalendarModeOptions.SELECT_DAY || calendarModel.mode === CalendarModeOptions.SELECT_WEEK"
      data-test="calendar-grid-day"
      class="calendar__menu__days-container flex flex-col">
      <CalendarHeaderDaySelector
        :month="calendarModel.currentPickerMonth"
        :mode="calendarModel.mode"
        :selectionMode="selectionMode"
        :year="calendarModel.currentPickerYear"
        data-test="calendar-header"
        @update:mode="updateMode"
        @update:month="updateMonth"
        @update:year="updateYear"
        @today="selectToday" />
      <ol
        data-test="calendar-menu-weekdays"
        class="calendar__menu__weekdays user-select-none">
        <li
          v-for="weekday of calendarModel.weekdayNames"
          :key="weekday"
          class="calendar__menu__weekday body--auxiliary-01">
          {{ weekday }}
        </li>
      </ol>
      <ol class="calendar__menu__dates user-select-none">
        <template v-for="day in calendarModel.daysOfLastMonth">
          <!-- eslint-disable vue/require-v-for-key -->
          <li
            v-if="day.isFirstDayOfWeek"
            :data-test="`calendar-week-number-${day.weekNumber}`"
            :class="{
              'calendar__menu__weekday--selected': day.isSelected && selectionMode === CalendarModeOptions.SELECT_WEEK,
              'calendar__menu__weekday--hover': hoveredWeekNumber === day.weekNumber,
            }"
            class="calendar__menu__weekday body--auxiliary-01 text-core-neutral-cold-50"
            @mouseenter="handleHover(day.weekNumber)"
            @mouseleave="handleHover(0)"
            @click="selectWeek(day.date, day.isWeekDisabled)">
            <span>{{ day.weekNumber }}</span>
          </li>
          <!-- eslint-disable vue/valid-v-for -->
          <CalendarDayNumber
            v-if="day.date"
            :date="day.date"
            :isCurrentDate="day.isCurrentDate"
            :isDisabled="day.isDisabled"
            :isEndDate="day.isEndDate"
            :isInRange="day.isInRange"
            :isSelected="day.isSelected"
            :isStartDate="day.isStartDate"
            :isLastDayOfWeek="day.isLastDayOfWeek"
            :isHoveredWeek="hoveredWeekNumber === day.weekNumber"
            :mode="calendarModel.mode"
            @mouseenter="handleHover(day.weekNumber)"
            @mouseleave="handleHover(0)"
            @click.stop="selectDate(day.date, isDateDisabled(day))" />
          <li v-else />
        </template>
        <template v-for="day in calendarModel.daysInMonth">
          <!-- eslint-disable vue/require-v-for-key -->
          <li
            v-if="day.isFirstDayOfWeek"
            :data-test="`calendar-week-number-${day.weekNumber}`"
            :class="{
              'calendar__menu__weekday--selected': day.isSelected && selectionMode === CalendarModeOptions.SELECT_WEEK,
              'calendar__menu__weekday--hover': hoveredWeekNumber === day.weekNumber,
            }"
            class="calendar__menu__weekday body--auxiliary-01 text-core-neutral-cold-50"
            @mouseenter="handleHover(day.weekNumber)"
            @mouseleave="handleHover(0)"
            @click="selectWeek(day.date, day.isWeekDisabled)">
            <span>{{ day.weekNumber }}</span>
          </li>
          <!-- eslint-disable vue/valid-v-for -->
          <CalendarDayNumber
            v-if="day.date"
            :date="day.date"
            :isCurrentDate="day.isCurrentDate"
            :isDisabled="day.isDisabled"
            :isEndDate="day.isEndDate"
            :isInRange="day.isInRange"
            :isSelected="day.isSelected"
            :isStartDate="day.isStartDate"
            :isLastDayOfWeek="day.isLastDayOfWeek"
            :isHoveredWeek="hoveredWeekNumber === day.weekNumber"
            :mode="calendarModel.mode"
            @mouseenter="handleHover(day.weekNumber)"
            @mouseleave="handleHover(0)"
            @click.stop="selectDate(day.date, isDateDisabled(day))" />
        </template>
        <template v-for="day in calendarModel.daysOfNextMonth">
          <!-- eslint-disable vue/valid-v-for -->
          <CalendarDayNumber
            v-if="day.date"
            :date="day.date"
            :isCurrentDate="day.isCurrentDate"
            :isDisabled="day.isDisabled"
            :isEndDate="day.isEndDate"
            :isInRange="day.isInRange"
            :isSelected="day.isSelected"
            :isStartDate="day.isStartDate"
            :isLastDayOfWeek="day.isLastDayOfWeek"
            :isHoveredWeek="hoveredWeekNumber === day.weekNumber"
            :mode="calendarModel.mode"
            @mouseenter="handleHover(day.weekNumber)"
            @mouseleave="handleHover(0)"
            @click.stop="selectDate(day.date, isDateDisabled(day))" />
          <li v-else />
        </template>
      </ol>
      <div
        v-if="range"
        class="calendar__menu__selection-container gap-4">
        <RangeSelectorSelectedDate
          :class="{ selected: isUpdatingStartDate }"
          :value="calendarModel.startDate"
          :label="t('common.messages.from')"
          :placeholder="t('common.messages.select')"
          dataTest="selected-start"
          @click.stop="updateStartDate" />
        <RangeSelectorSelectedDate
          :class="{ selected: isUpdatingEndDate }"
          :value="calendarModel.endDate"
          :label="t('common.messages.to')"
          :placeholder="t('common.messages.select')"
          dataTest="selected-end"
          @click.stop="updateEndDate" />
      </div>
    </div>
    <CalendarHeaderMonthSelector
      v-show="calendarModel.mode===CalendarModeOptions.SELECT_MONTH"
      :mode="calendarModel.mode"
      :month="calendarModel.currentPickerMonth"
      :monthsInYear="calendarModel.monthsInYear"
      :selectionMode="selectionMode"
      :year="calendarModel.currentPickerYear"
      data-test="calendar-grid-month"
      @monthSelected="selectMonth"
      @update:mode="updateMode"
      @update:month="updateMonth"
      @update:year="updateYear"
      @today="selectToday" />
    <CalendarHeaderYearSelector
      v-show="calendarModel.mode===CalendarModeOptions.SELECT_YEAR"
      :mode="calendarModel.mode"
      :month="calendarModel.currentPickerMonth"
      :selectionMode="selectionMode"
      :year="calendarModel.currentPickerYear"
      :disablePastDays="disablePastDays"
      data-test="calendar-grid-year"
      @update:mode="updateMode"
      @update:month="updateMonth"
      @update:year="updateYear"
      @select:year="selectYear"
      @today="selectToday" />
  </div>
</template>

<style scoped>
:deep(.capitalize-text) {
  text-transform: capitalize;
}

:deep(.uppercase-text) {
  text-transform: uppercase;
}

.calendar__menu {
  --cell-size: 2rem;
  --cell-gap: 2px;

  border: 1px solid var(--core-neutral-cold-90);
  border-radius: var(--s-1);
  width: 19rem;
}

:deep(.core-calendar__menu__header-action) {
  text-transform: capitalize;
}

.calendar__menu__days-container, .calendar__menu ol {
  gap: var(--cell-gap);
}

.calendar__menu ol {
  display: grid;
  list-style: none;
  padding: 0;
  margin: 0;
}

.calendar__menu li {
  display: flex;
}

.calendar__menu__weekdays {
  grid-template-columns: repeat(7, var(--cell-size));
  grid-template-rows: repeat(1, var(--cell-size));
  align-self: flex-end;
}

.calendar__menu__dates {
  grid-template-columns: repeat(8, var(--cell-size));
}

.calendar__menu__weekday {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--cell-size);
  position: relative;
}

.calendar__menu__week {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--cell-size);
}

.calendar__menu__week:hover {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--cell-size);
  background-color: var(--core-primary-80);
}

.calendar__menu__weekday--hover::after,
.calendar__menu__weekday--selected::before,
.calendar__menu__weekday--hover::before {
  content: '';
  position: absolute;
  top: 0;
  left: calc(1 * var(--cell-gap));
  height: var(--cell-size);
  width: calc(var(--cell-size) + calc(2 * var(--cell-gap)));
  background-color: var(--core-primary-90);
  z-index: 2;
  border-radius: 4px 0px 0px 4px;
}

.calendar__menu__weekday--selected::before {
  top: 0.14rem;
  left: 0.25rem;
  height: calc(-0.28rem + var(--cell-size));
  background-color: var(--core-primary-80);
  z-index: 3;
}

.calendar__menu__weekday span {
  z-index: 3;
}

.calendar__menu__selection-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--s-4);
}
</style>
